'use client';

import { useCallback, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, XIcon } from 'lucide-react';
import { motion } from 'motion/react';
import Image from 'next/image';
import { SectionLayout } from '@/components/shared/section-layout';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { useIsMobile } from '@/hooks/use-mobile';
import SectionHeader from '@/components/shared/section-header';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import CarouselButton from '@/components/shared/carousel-button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { VA_EVENTS } from '@/lib/analytics/event-catalog';
import { vaTrack } from '@/lib/analytics/vercel-events';
import { cn } from '@/lib/utils';
import { type SanityProject } from '@/sanity/project-data';
import type { BeforeAfterSectionContent } from '@/sanity/lib/sections';
import CardExpandButton from '../../shared/card-expand-button';

function BeforeAfterLightboxMetaOverlay({
  label,
  comparisonTitle,
  projectName,
  location,
}: {
  label: 'Before' | 'After';
  comparisonTitle: string;
  projectName: string;
  location: string | null | undefined;
}) {
  const place = location?.trim() || null;
  const title = comparisonTitle.trim();
  const subtitle = [projectName.trim(), place].filter(Boolean).join(' • ');
  const showFooter = title || subtitle;

  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-20 bg-[linear-gradient(180deg,rgba(0,0,0,0.24)_0%,rgba(0,0,0,0)_26%,rgba(0,0,0,0)_38%,rgba(0,0,0,0.82)_100%)]" />
      <Badge className="pointer-events-none absolute top-4 left-4 z-40 h-6 w-fit rounded-full border border-white/20 bg-white/10 px-3 text-[11px] font-bold tracking-widest text-white uppercase backdrop-blur-sm">
        {label}
      </Badge>
      {showFooter ? (
        <div className="pointer-events-none absolute bottom-4 left-4 z-40 pr-10 sm:pr-14">
          {title ? (
            <h2 className="line-clamp-2 text-base leading-none text-white sm:text-lg">{title}</h2>
          ) : null}
          {subtitle ? (
            <p className={cn('line-clamp-2 text-sm leading-snug text-white/75', title && 'mt-1')}>
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function BeforeAfterImageLightbox({
  src,
  alt,
  label,
  comparisonTitle,
  projectName,
  location,
}: {
  src: string;
  alt: string;
  label: 'Before' | 'After';
  comparisonTitle: string;
  projectName: string;
  location: string | null | undefined;
}) {
  const [open, setOpen] = useState(false);
  const displayAlt = alt.trim() || `${label} photo`;
  const place = location?.trim() || null;
  const triggerAria = [
    comparisonTitle.trim() || null,
    projectName.trim() || null,
    place,
    `${label} preview`,
    displayAlt,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`Open preview: ${triggerAria}`}
          className={cn(
            'group relative aspect-video w-full overflow-hidden rounded-xl text-left',
            'cursor-pointer focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-white/40',
          )}
        >
          <Image
            src={src}
            alt={displayAlt}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px"
            className="object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          />
          <CardExpandButton />
          <Badge className="absolute left-4 top-4 rounded-full border border-white/20 bg-white/10 px-3 text-[11px] h-6 font-bold uppercase tracking-widest text-white backdrop-blur-sm w-fit pointer-events-none">
            {label}
          </Badge>
        </button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'grid-rows-[auto] h-auto w-full gap-0 overflow-visible border-none bg-transparent p-0!',
          'max-md:max-h-dvh max-md:max-w-[100vw] max-md:w-screen max-md:rounded-none',
          'md:max-w-5xl! md:rounded-xl',
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>
            {[comparisonTitle.trim(), projectName.trim(), place, label]
              .filter(Boolean)
              .join(' — ') || displayAlt}
          </DialogTitle>
          <DialogDescription>
            {label} preview — full-size image
            {comparisonTitle.trim() ? ` — ${comparisonTitle.trim()}` : ''}
            {projectName.trim() ? ` — ${projectName.trim()}` : ''}
            {place ? ` — ${place}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogClose asChild>
          <button
            type="button"
            aria-label="Close"
            className="absolute top-3 right-3 z-60 flex size-8 cursor-pointer items-center justify-center rounded-full bg-[#948B7F] text-white transition-all duration-300 ease-in-out hover:bg-[#BFB7A1] sm:size-10"
          >
            <XIcon className="size-4 sm:size-5" />
          </button>
        </DialogClose>
        <div
          className={cn(
            'relative flex w-full items-center justify-center overflow-hidden rounded-xl border border-white/20 bg-black',
            'max-md:rounded-none max-md:border-x-0',
          )}
        >
          <Image
            src={src}
            alt={displayAlt}
            width={2400}
            height={1600}
            className="h-auto w-full object-contain"
            sizes="(max-width: 768px) 96vw, min(1920px, 94vw)"
          />
          <BeforeAfterLightboxMetaOverlay
            label={label}
            comparisonTitle={comparisonTitle}
            projectName={projectName}
            location={location}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BeforeAfter({
  projects,
  content,
}: {
  projects: SanityProject[];
  content?: BeforeAfterSectionContent | null;
}) {
  const isMobile = useIsMobile();
  const {
    sectionCardRevealVariants,
    sectionGridRevealVariants,
    sectionSequenceBlockVariants,
    sectionSequenceRevealVariants,
  } = useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const comparisons = useMemo(
    () =>
      projects.flatMap((project) =>
        project.beforeAfter.flatMap((comparison) => {
          const title = comparison.title?.trim();
          if (!title) return [];

          return [
            {
              title,
              projectTitle: project.title,
              location: project.location,
              beforeSrc: comparison.beforeSrc,
              afterSrc: comparison.afterSrc,
              beforeAlt: comparison.beforeAlt,
              afterAlt: comparison.afterAlt,
            },
          ];
        }),
      ),
    [projects],
  );
  const selectedComparison = comparisons[selectedIndex];

  const showPrevious = useCallback(() => {
    vaTrack(VA_EVENTS.CAROUSEL_PREV, { section: 'before_after' });
    setSelectedIndex((index) => (index === 0 ? comparisons.length - 1 : index - 1));
  }, [comparisons.length]);
  const showNext = useCallback(() => {
    vaTrack(VA_EVENTS.CAROUSEL_NEXT, { section: 'before_after' });
    setSelectedIndex((index) => (index === comparisons.length - 1 ? 0 : index + 1));
  }, [comparisons.length]);

  if (!content || content.show === false || content.showCarousel === false || !comparisons.length) {
    return null;
  }

  const { header } = content;
  const showControls = content.showCarouselControls !== false;
  const { primary, secondary } = sectionCtaPairFromContent(content.showCTA, content.ctaButtons);

  return (
    <SectionLayout
      id="before-vs-after"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-neutral-900 dark:bg-black border-y py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        className="space-y-6 sm:space-y-12"
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
      >
        {content.showHeader !== false ? (
          <SectionHeader
            sequenceChild
            content={{
              eyebrow: header?.tagline,
              description: header?.subheading,
            }}
            titleSlot={
              <h2 className="text-3xl sm:text-5xl leading-tight font-light tracking-tight text-white">
                <SanityInlineText
                  blocks={header?.heading}
                  emphasisClassName="italic font-normal text-[#FCFAD7] dark:text-[#FCFAD7]"
                />
              </h2>
            }
            eyebrowClassName="text-[#FCFAD7] dark:text-[#FCFAD7]"
            emphasisClassName="text-[#FCFAD7] dark:text-[#FCFAD7]"
            headingClassName="text-white!"
            subheadingClassName="text-muted-foreground"
          />
        ) : null}
        {primary ? (
          <motion.div variants={sectionSequenceBlockVariants}>
            <SectionCtaPair
              primary={primary}
              secondary={secondary}
              trackingLocation="before_after"
            />
          </motion.div>
        ) : null}

        <motion.div variants={sectionGridRevealVariants}>
          <motion.div
            variants={sectionCardRevealVariants}
            className="mb-6 flex items-center justify-between gap-4"
          >
            <div>
              <p className="text-base sm:text-lg leading-none font-medium text-white">
                {selectedComparison?.title}
              </p>
              <p className="mt-1 text-sm leading-none text-muted-foreground">
                {[selectedComparison?.projectTitle, selectedComparison?.location]
                  .filter(Boolean)
                  .join(' • ')}
              </p>
            </div>
            {showControls ? (
              <div className="hidden min-[1324px]:flex items-center gap-1">
                <CarouselButton
                  icon={ChevronLeft}
                  label="Show previous project"
                  onClick={showPrevious}
                />
                <CarouselButton icon={ChevronRight} label="Show next project" onClick={showNext} />
              </div>
            ) : null}
          </motion.div>

          <motion.div variants={sectionCardRevealVariants} className="grid">
            {comparisons.map((comparison, index) => (
              <motion.div
                key={`${comparison.beforeSrc}-${comparison.afterSrc}`}
                aria-hidden={selectedIndex !== index}
                className={`col-start-1 row-start-1 ${
                  selectedIndex === index ? 'pointer-events-auto' : 'pointer-events-none'
                }`}
                initial={false}
                animate={{
                  opacity: selectedIndex === index ? 1 : 0,
                  filter: selectedIndex === index || isMobile ? 'blur(0px)' : 'blur(2px)',
                }}
                transition={{ duration: isMobile ? 0.28 : 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <BeforeAfterImageLightbox
                    src={comparison.beforeSrc}
                    alt={comparison.beforeAlt ?? ''}
                    label="Before"
                    comparisonTitle={comparison.title}
                    projectName={comparison.projectTitle}
                    location={comparison.location}
                  />
                  <BeforeAfterImageLightbox
                    src={comparison.afterSrc}
                    alt={comparison.afterAlt ?? ''}
                    label="After"
                    comparisonTitle={comparison.title}
                    projectName={comparison.projectTitle}
                    location={comparison.location}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>

          {showControls ? (
            <motion.div
              variants={sectionCardRevealVariants}
              className="mt-4 sm:mt-6 flex items-center justify-start gap-1 min-[1324px]:hidden"
            >
              <CarouselButton
                icon={ChevronLeft}
                label="Show previous project"
                onClick={showPrevious}
              />
              <CarouselButton icon={ChevronRight} label="Show next project" onClick={showNext} />
            </motion.div>
          ) : null}
        </motion.div>
      </motion.div>
    </SectionLayout>
  );
}
